// src/components/common/RaFilterPopover.tsx
//
// Bridges the standard SectionHeader (which lives OUTSIDE the React-Admin <List>)
// with React-Admin's filter system (which must live INSIDE the <List> to access
// the list context / filter form).
//
// - FilterPopoverProvider wraps both the SectionHeader and the <List> at page level.
// - SectionHeader reads the context to drive its "Filters" button (open state,
//   anchor element, active-filter badge) and optional inline search field.
// - RaFilterPopover is rendered as a child of <List>; it renders the RA filter
//   inputs inside a Popover (via <FilterForm>) and keeps the inline search in sync
//   with the list filters.
//
// Result: every RA list gets the exact same popup affordance as the self-managed
// screens (Events / Alerts), without rewiring each screen's filter logic.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Popover, useTheme } from '@mui/material';
import { FilterLiveForm, useListContext } from 'react-admin';

export interface FilterPopoverState {
  open: boolean;
  setOpen: (open: boolean) => void;
  anchorEl: HTMLElement | null;
  setAnchorEl: (el: HTMLElement | null) => void;
  activeCount: number;
  setActiveCount: (n: number) => void;
  hasFilters: boolean;
  setHasFilters: (v: boolean) => void;
  searchValue: string;
  setSearchValue: (v: string) => void;
  /** Refresh action registered by the active content (e.g. a Logs tab), surfaced
   *  in the SectionHeader right cluster. */
  refreshFn?: () => void;
  refreshing?: boolean;
  setRefresh: (r: { onRefresh?: () => void; refreshing?: boolean }) => void;
}

const FilterPopoverContext = createContext<FilterPopoverState | null>(null);

export const useFilterPopover = (): FilterPopoverState | null =>
  useContext(FilterPopoverContext);

/** Wrap the SectionHeader + <List> of an RA screen so they share filter state. */
export const FilterPopoverProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeCount, setActiveCount] = useState(0);
  const [hasFilters, setHasFilters] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [refresh, setRefreshState] = useState<{ onRefresh?: () => void; refreshing?: boolean }>({});

  // Idempotent: bail out when the registered refresh is unchanged, so a consumer
  // re-running this effect (because the context value changed) cannot loop.
  const setRefresh = useCallback((r: { onRefresh?: () => void; refreshing?: boolean }) => {
    setRefreshState((prev) =>
      prev.onRefresh === r.onRefresh && prev.refreshing === r.refreshing ? prev : r,
    );
  }, []);

  const value = useMemo(
    () => ({
      open, setOpen,
      anchorEl, setAnchorEl,
      activeCount, setActiveCount,
      hasFilters, setHasFilters,
      searchValue, setSearchValue,
      refreshFn: refresh.onRefresh,
      refreshing: refresh.refreshing,
      setRefresh,
    }),
    [open, anchorEl, activeCount, hasFilters, searchValue, refresh, setRefresh],
  );

  return (
    <FilterPopoverContext.Provider value={value}>
      {children}
    </FilterPopoverContext.Provider>
  );
};

interface RaFilterPopoverProps {
  /** RA filter input elements. Omit for search-only screens (sync only, no button). */
  children?: React.ReactNode;
  /** Filter keys to ignore when counting active filters (defaults like hotelId/dateFrom). */
  ignoreKeys?: string[];
  /** Filter source bound to the inline search field (e.g. "q"). */
  searchSource?: string;
}

/**
 * Rendered INSIDE <List>. Hosts the RA filter inputs in a Popover anchored to the
 * SectionHeader's Filters button, and mirrors the inline search into the list filters.
 * Uses RA's <FilterLiveForm> so the inputs apply to the list as they change, without
 * requiring the list's FilterContext.
 */
export const RaFilterPopover: React.FC<RaFilterPopoverProps> = ({
  children,
  ignoreKeys = [],
  searchSource = 'q',
}) => {
  const theme = useTheme();
  const ctx = useFilterPopover();
  const { filterValues = {}, setFilters, displayedFilters } = useListContext();

  const hasChildren = React.Children.toArray(children).length > 0;

  // Depend on the STABLE context setters, not on the context value object (which
  // is recreated on every provider state change) — see SelfFilterPopover.
  const setHasFilters = ctx?.setHasFilters;
  const setActiveCount = ctx?.setActiveCount;

  // Tell the header whether a Filters button should be shown (only when there
  // are popover filters; search-only screens render this just for search sync).
  useEffect(() => {
    setHasFilters?.(hasChildren);
    return () => setHasFilters?.(false);
  }, [setHasFilters, hasChildren]);

  // Count active filters (excluding defaults and the inline search source).
  useEffect(() => {
    if (!setActiveCount) return;
    const ignored = new Set([...ignoreKeys, searchSource]);
    const count = Object.entries(filterValues).filter(([k, v]) =>
      !ignored.has(k) && v !== undefined && v !== null && v !== '' &&
      !(Array.isArray(v) && v.length === 0),
    ).length;
    setActiveCount(count);
  }, [setActiveCount, filterValues, ignoreKeys, searchSource]);

  // Keep the inline search (header) in sync with the list filter (debounced).
  const lastSearch = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!ctx) return;
    if (lastSearch.current === undefined) {
      // Initialise from any existing filter value on first mount.
      lastSearch.current = (filterValues[searchSource] as string) ?? '';
      if (ctx.searchValue === '' && lastSearch.current) ctx.setSearchValue(lastSearch.current);
      return;
    }
    if (ctx.searchValue === lastSearch.current) return;
    lastSearch.current = ctx.searchValue;
    const handle = setTimeout(() => {
      const next = { ...filterValues };
      if (ctx.searchValue) next[searchSource] = ctx.searchValue;
      else delete next[searchSource];
      setFilters(next, displayedFilters);
    }, 350);
    return () => clearTimeout(handle);
  }, [ctx?.searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ctx || !hasChildren) return null;

  return (
    <Popover
      open={ctx.open}
      anchorEl={ctx.anchorEl}
      onClose={() => ctx.setOpen(false)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            p: 2,
            borderRadius: theme.custom?.radius?.card ?? '10px',
            maxWidth: 680,
            boxShadow: theme.custom?.shadows?.medium,
          },
        },
      }}
    >
      <Box
        sx={{
          '& .MuiInputBase-root': {
            minHeight: '36px !important',
            height: 36,
            fontSize: '0.8125rem',
            backgroundColor: 'background.paper',
            borderRadius: theme.custom?.radius?.small ?? '8px',
          },
          '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          '& .MuiFormControl-root': { m: 0 },
          '& .MuiFormHelperText-root': { display: 'none' },
          // FilterLiveForm renders a <form>; lay its inputs out in a wrapping row.
          '& form': {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
            minHeight: 'auto',
            margin: 0,
          },
          '& form > *': { mr: '0 !important', mb: '0 !important' },
        }}
      >
        <FilterLiveForm>
          {children}
        </FilterLiveForm>
      </Box>
    </Popover>
  );
};

export default RaFilterPopover;
